import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { FIGURE_TONE, type FigureTone } from '@/components/ui/FigureTile';
import { cn } from '@/lib/utils';

export interface OverviewCardProps {
  title: string;
  /** The mark in the header's tinted tile — a lucide icon at `h-4 w-4`. */
  icon: ReactNode;
  /** Which tint that tile wears. The four status names keep their meaning. */
  tone?: FigureTone;
  /** A figure beside the title — how many notices are on the board. */
  count?: number;
  /**
   * A control on the right of the header — the attendance card's timeframe
   * select. Takes the place of `viewAllTo`, never sits beside it: two things
   * competing for the same corner is how a header stops being scannable.
   */
  action?: ReactNode;
  /** Where the whole section lives. Drawn as "View all" when there is no `action`. */
  viewAllTo?: string;
  /**
   * What that link is announced as — "View all class notices".
   *
   * Without it a screen reader reads five links all called "View all" and none
   * of them says what it opens.
   */
  viewAllLabel?: string;
  children: ReactNode;
  /** A link across the foot of the card — "View full timetable". */
  footer?: ReactNode;
  className?: string;
  /** For a body that sets its own padding, or scrolls. */
  bodyClassName?: string;
}

/**
 * One panel on the parent's overview.
 *
 * Every block on that screen is the same object — a tinted header strip with a
 * mark, a title and one control, a body, and sometimes a link across the foot —
 * so it is one component with props rather than five cards that would drift
 * apart the first time one of them was touched (CLAUDE.md §3).
 *
 * The tint comes from `FigureTile`'s tone table, which is what the tiles above
 * the card are already painted from: a section whose figure tile is amber and
 * whose panel is indigo reads as two unrelated things.
 */
export function OverviewCard({
  title,
  icon,
  tone = 'accent',
  count,
  action,
  viewAllTo,
  viewAllLabel,
  children,
  footer,
  className,
  bodyClassName,
}: OverviewCardProps) {
  return (
    <section
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80 px-4  py-3.5 ">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon && (
            <span
              className={cn(
                'grid h-8 w-8 shrink-0 place-items-center rounded-xl',
                FIGURE_TONE[tone],
              )}
              aria-hidden
            >
              {icon}
            </span>
          )}
          <h2 className="min-w-0 truncate text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-white">
            {title}
          </h2>
          {count !== undefined && count > 0 && (
            <span className="shrink-0 rounded-full bg-indigo-600 dark:bg-indigo-500 px-2 py-0.5 text-xs font-bold text-white">
              {count}
            </span>
          )}
        </div>

        {action ??
          (viewAllTo && (count === undefined || count > 0) && (
            <Link
              to={viewAllTo}
              aria-label={viewAllLabel ?? `View all — ${title}`}
              className="shrink-0 text-xs sm:text-sm font-semibold text-indigo-600 dark:text-indigo-400 transition-colors hover:text-indigo-700 dark:hover:text-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              View all
            </Link>
          ))}
      </header>

      <div className={cn('flex flex-1 flex-col gap-3.5 p-4 ', bodyClassName)}>
        {children}
      </div>

      {footer && (
        <div className="border-t border-slate-100 dark:border-slate-800/80 px-4 sm:px-5 py-3">
          {footer}
        </div>
      )}
    </section>
  );
}
